import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EventModel } from '../models/EventModel';
import { DateUtil } from '../utils/DateUtil';
import { formatPrice } from '../utils/PriceUtil';
import FavoriteButton from './FavoritesButton';
import ImageWithPlaceholder from './ImageWithPlaceholder';
import styles from './VenueEventListRow.module.css';

const venueTimezone = 'America/Toronto';

function formatOccurrence(date: string, includeWeekday = false): string {
    const formatted = new Intl.DateTimeFormat('fr-CA', {
        timeZone: venueTimezone,
        ...(includeWeekday ? { weekday: 'long' as const } : {}),
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
    }).format(new Date(date)).replace(' h 00', ' h');

    return includeWeekday
        ? formatted.charAt(0).toLocaleUpperCase('fr-CA') + formatted.slice(1)
        : formatted;
}

interface VenueEventListRowProps {
    event: EventModel;
}

export default function VenueEventListRow({ event }: VenueEventListRowProps) {
    const [showDates, setShowDates] = useState(false);
    const path = `/details/${event.id}`;
    const dates = useMemo(
        () => event.dates
            .map((dateEntry) => dateEntry.date)
            .filter(Boolean)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        [event.dates]
    );
    const recurrenceText = DateUtil.recurringRulesToTexts(
        event.recurringRules ?? [],
        !event.timeUnkown
    )[0];
    const price = formatPrice(event.price);
    const previewDates = dates.slice(0, 3);
    const hasSeveralDates = dates.length > 1;

    return <article className={styles.row}>
        <div className={styles.imageWrap}>
            <Link to={path} aria-label={`Voir les détails de ${event.title}`}>
                <ImageWithPlaceholder className={styles.image} src={event.imageUrl} alt={event.title} />
            </Link>
        </div>

        <div className={styles.content}>
            <div className={styles.categoryLine}>
                {event.categories[0]?.name && <span>{event.categories[0].name}</span>}
                {price && <strong>{price}</strong>}
            </div>
            <Link to={path} className={styles.title}>{event.title}</Link>

            {recurrenceText && <p className={styles.recurrence}>{recurrenceText}</p>}
            {hasSeveralDates && (
                <p className={styles.preview}>
                    <span>Prochaines dates : </span>
                    {previewDates.map((date) => formatOccurrence(date)).join(' · ')}
                </p>
            )}

            <div className={styles.actions}>
                {hasSeveralDates && (
                    <button
                        type="button"
                        className={styles.datesButton}
                        aria-expanded={showDates}
                        onClick={() => setShowDates((current) => !current)}
                    >
                        {showDates ? 'Masquer les dates' : `Voir les ${dates.length} dates`}
                    </button>
                )}
                <Link to={path} className={styles.detailsLink}>Voir les détails</Link>
            </div>

            {showDates && (
                <ul className={styles.dateList}>
                    {dates.map((date) => <li key={date}>{formatOccurrence(date, true)}</li>)}
                </ul>
            )}
        </div>

        <FavoriteButton eventId={event.id} />
    </article>;
}
